import type { SyntheticEvent } from 'react';
import type { Nullable, StringMap } from 'corgi-types';

import { dispatchTrackingEventGA4 } from './dispatchTrackingEventGA4';

export interface TrackingEvent extends SyntheticEvent {
  target: HTMLElement | HTMLAnchorElement;
  currentTarget: HTMLLinkElement & { action: string; alt: string };
  nativeEvent: Event & {
    trackingPreventDefault?: boolean;
  };
}

export interface TrackerProps {
  href: string;
}

export type TrackingEventData = { event: string } & StringMap<string>;

export interface PageAppTrackerProps {
  name: string;
  placement: string;
  position?: number;
  dyDecisionId?: string;
  dyVariationId?: number;
  visitorHash?: string;
}

export interface CMSTrackerProps {
  onClick?: (event: TrackingEvent) => void;
  onSubmit?: (event: TrackingEvent) => void;
  [key: string]: any;
}

interface EventTrackingProps {
  componentProps: Omit<CMSTrackerProps, 'onClick' | 'onSubmit'>;
  eventData: TrackingEventData;
  filterElementsList: Nullable<string[]>;
}

const callAll =
  (...fns: Array<(...args: any[]) => any>) =>
  (...args: any[]) =>
    fns.forEach(fn => fn && fn(...args));

function getURLPath(url) {
  url = new URL(url);

  return url.href.replace(url.origin, '');
}

const isEventPrevented = (event: TrackingEvent) =>
  event.defaultPrevented || event.nativeEvent.trackingPreventDefault;

/**
 * General method to dispatch tracking events from component.
 * It will try to get as information from the DOM event as possible,
 * otherwise will fall back to component props or overrides
 */
function dispatchTrackingEvent(
  componentProps: TrackerProps,
  eventData: TrackingEventData,
  event: TrackingEvent
) {
  if (isEventPrevented(event)) {
    return;
  }

  const targetUrl = event.currentTarget.action || event.currentTarget.href || componentProps.href;

  const label =
    event.currentTarget.alt ||
    event.currentTarget.getAttribute('aria-label') ||
    event.currentTarget.textContent;

  const trackingData = {
    ...(targetUrl && { target_url: getURLPath(targetUrl) }),
    ...(event.type !== 'submit' && label && { label }),
    ...eventData,
  };

  dispatchTrackingEventGA4(trackingData);
}

/**
 * More specific tracker for CMS blocks in top navigation. Handles
 * a couple of corner cases when tracking inner html of a component
 * inserted with dangerouslySetInnerHTML.
 *
 * Might be considered a hack for now, probably can be changed when
 * those CMS blocks are migrated to Prismic, as this will
 * allow us to attach listeners directly to elements
 */
function dispatchCMSTrackingEvent(eventTrackingProps: EventTrackingProps, event: TrackingEvent) {
  const { componentProps, eventData, filterElementsList } = eventTrackingProps;
  // If the event was prevented, or the tracking dispatch was prevented
  // with a special flag on an native event object, we will stop here
  if (isEventPrevented(event)) {
    return;
  }

  // If filter element list was provided and our target element is not
  // in the list, we will stop here
  if (filterElementsList !== null && !filterElementsList.includes(event.target.nodeName)) {
    return;
  }

  const target =
    event.target.parentNode.nodeName === 'A'
      ? (event.target.parentNode as HTMLAnchorElement)
      : event.target;

  const targetUrl = ('href' in target && target.href) || componentProps.href;

  const image = target.getElementsByTagName('img')[0];

  const label =
    (image && image.alt) || event.currentTarget.getAttribute('aria-label') || target.textContent;

  const trackingData = {
    ...(targetUrl && { target_url: getURLPath(targetUrl) }),
    ...(label && { label }),
    ...eventData,
  };

  dispatchTrackingEventGA4(trackingData);
}

export function getTrackerProps(
  { onClick, onSubmit, ...componentProps }: CMSTrackerProps = {},
  eventData = {}
) {
  return {
    onClick: callAll(onClick, dispatchTrackingEvent.bind(null, componentProps, eventData)),
    onSubmit: callAll(onSubmit, dispatchTrackingEvent.bind(null, componentProps, eventData)),
    ...componentProps,
  };
}

export function getCMSTrackerProps(
  { onClick, onSubmit, ...componentProps }: CMSTrackerProps = {},
  eventData = {},
  filterElementsList: EventTrackingProps['filterElementsList'] = null
) {
  return {
    onClick: callAll(
      onClick,
      dispatchCMSTrackingEvent.bind(null, { componentProps, eventData, filterElementsList })
    ),
    onSubmit: callAll(
      onSubmit,
      dispatchCMSTrackingEvent.bind(null, { componentProps, eventData, filterElementsList })
    ),
    ...componentProps,
  };
}
